import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Box, Grid, Alert } from '@mui/material';
import { ContentModal, ControlledCheckbox, ControlledTextField, ProgressButton } from '@localstack/ui';
import { CreateInvitationRequest } from '@localstack/types';
import { VALIDATION_RULES } from '@localstack/services';
import { USER_AND_LICENSES_TEST_IDS } from '@localstack/constants';

export interface InviteMemberModalProps {
  onClose: () => void;
  disallowInvites?: boolean;
  onConfirm: (invitation: CreateInvitationRequest) => Promise<void>;
}

export const InviteMemberModal = ({
  onClose,
  onConfirm,
  disallowInvites = false,
}: InviteMemberModalProps): JSX.Element => {
  const [isBusy, setIsBusy] = useState(false);

  const { control, handleSubmit } = useForm<CreateInvitationRequest>({
    mode: 'all',
    defaultValues: { join_settings: { auto_assign_license: true } },
  });

  const submitInvitation = useCallback(
    async (data: CreateInvitationRequest) => {
      setIsBusy(true);
      try {
        // todo: update BE to make the name optional on the request level
        await onConfirm({ ...data, name: data.name ?? data.email });
        onClose();
      } catch {
        // error already surfaced via the caller's own error handling; keep the dialog open to retry
      } finally {
        setIsBusy(false);
      }
    },
    [onConfirm, onClose],
  );

  return (
    <ContentModal
      modalMaxWidth="sm"
      open
      hideDialogActions
      onClose={onClose}
      fullWidth
      backdropBlurIntensity={3}
      dialogTitle="Invite new member to the workspace"
      PaperProps={{ 'data-testId': USER_AND_LICENSES_TEST_IDS.INVITE_USER_MODAL }}
      dialogContent={
        <Box>
          <Box mb={2}>
            <Alert severity="info">Invitations automatically expire after 30 days.</Alert>
          </Box>
          <form onSubmit={handleSubmit((data) => submitInvitation(data))}>
            <Grid item container spacing={2} xs={12} direction="column">
              <Grid item xs={12} sm={6}>
                <ControlledTextField
                  control={control}
                  name="email"
                  required
                  variant="outlined"
                  label="Email"
                  size="small"
                  fullWidth
                  rules={VALIDATION_RULES.email}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ControlledCheckbox
                  name="join_settings.auto_assign_license"
                  label={<Box>Automatically assign a license when the user joins (subject to availability)</Box>}
                  color="primary"
                  control={control}
                />
              </Grid>
              <Grid item>
                <ProgressButton
                  variant="contained"
                  color="primary"
                  size="small"
                  type="submit"
                  disabled={disallowInvites}
                  loading={isBusy}
                >
                  Invite to Workspace
                </ProgressButton>
              </Grid>
            </Grid>
          </form>
        </Box>
      }
    />
  );
};
